import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import type { TranslationKey } from 'src/types';
import { Button } from 'src/components/atoms';
import { BadgeVariant } from 'src/components/atoms/Badge/Badge.styles';
import { isValidDate } from 'src/utils/time';
import { useRedirect } from '../../../hooks';
import {
  BoldHeader,
  ButtonWrapper,
  HeaderRow,
  ReportDetail,
  ReportDetailColumn,
  ReportImage,
  Row,
  StyledBadge,
  SubmissionType,
  Top,
} from '../PublicReportDetails/PublicReportDetails.styles';

interface Props {
  style?: React.CSSProperties;
  organisation: string;
  reportId: string;
  category: string;
  displayId: string;
  status: string;
  created: string;
  includeStatus?: boolean;
  includeButton?: boolean;
  sidebar?: boolean;
  evidenceButtonLabel: string;
}

const PublicReportDetails: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { showRedirect, redirectTo, redirect } = useRedirect();

  return (
    <Row>
      {showRedirect && (
        <Redirect data-testid={redirectTo} to={`${redirectTo}`} push />
      )}
      <ReportImage
        className="public-report-card-image"
        src="/assets/NavyBlueReport.svg"
        alt="Report Icon"
        small={props.sidebar || false}
      />

      <Top>
        <HeaderRow>
          <SubmissionType>{props.category}</SubmissionType>
          {props.includeStatus && (
            <StyledBadge
              variant={props.status.toLowerCase() as BadgeVariant}
              hideWhenBig={props.sidebar || false}
              text={t(`ReportStatus.${props.status}` as TranslationKey)}
              data-testid={'public-report-status'}
            />
          )}
        </HeaderRow>

        <Row>
          <ReportDetailColumn fullWidth={props.sidebar || false}>
            <ReportDetail data-testid={'public-report-id'}>
              <BoldHeader>{t('public.PublicReportCard.reportId')}</BoldHeader>
              {` ${props.displayId}`}
            </ReportDetail>
            <ReportDetail data-testid={'public-report-submitted-date'}>
              <BoldHeader>{t('public.PublicReportCard.submitted')}</BoldHeader>
              {`: ${isValidDate(props.created) ? props.created : ''}`}
            </ReportDetail>
          </ReportDetailColumn>
        </Row>
      </Top>
      <ButtonWrapper>
        {props.includeButton && (
          <Button
            variant="primary"
            size="medium"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              redirect(
                `/${props.organisation}/open-reporting/messages/${props.reportId}`,
              );
            }}
            data-testid={'message-case-manager'}
            width="190px"
          >
            {t('public.PublicReportCard.messageCaseManager')}
          </Button>
        )}
        <Button
          variant="secondary"
          size="medium"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            redirect(
              `/${props.organisation}/open-reporting/more-evidence/${props.reportId}`,
            );
          }}
          data-testid={'add-more-evidence'}
          width="190px"
          noPadding
        >
          {props.evidenceButtonLabel}
        </Button>
      </ButtonWrapper>
    </Row>
  );
};

export default PublicReportDetails;
